// Device-local storage for offline use: saved PDFs (IndexedDB), tests the
// student opened (so they can be taken again without internet), and test
// submissions waiting for the network.
import type { MockTest } from '@/data/mockQuestions';

const DB = 'isc-offline';
const STORE = 'files';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveFileBlob(id: string, blob: Blob): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, id);
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
  } catch { /* storage unavailable */ }
}

export async function getFileBlob(id: string): Promise<Blob | null> {
  try {
    const db = await openDb();
    return await new Promise((res) => {
      const req = db.transaction(STORE).objectStore(STORE).get(id);
      req.onsuccess = () => res((req.result as Blob) ?? null);
      req.onerror = () => res(null);
    });
  } catch { return null; }
}

export async function clearFileBlobs(): Promise<void> {
  try {
    const db = await openDb();
    db.transaction(STORE, 'readwrite').objectStore(STORE).clear();
  } catch { /* ignore */ }
}

// ---------- Offline tests ----------
const TESTS_KEY = 'isc-offline-tests';
const MAX_TESTS = 15;
export const OFFLINE_TESTS_EVENT = 'isc-offline-tests-updated';

export interface OfflineTest { test: MockTest; savedAt: number }

export function getOfflineTests(): OfflineTest[] {
  try {
    const v = JSON.parse(localStorage.getItem(TESTS_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

export function saveOfflineTest(test: MockTest): void {
  const has = (test.sections || []).some((s) => (s.questions?.length || 0) > 0);
  if (!has) return;
  try {
    const rest = getOfflineTests().filter((t) => t.test.id !== test.id);
    localStorage.setItem(TESTS_KEY, JSON.stringify([{ test, savedAt: Date.now() }, ...rest].slice(0, MAX_TESTS)));
    window.dispatchEvent(new CustomEvent(OFFLINE_TESTS_EVENT));
  } catch { /* quota */ }
}

export function removeOfflineTest(id: string): void {
  localStorage.setItem(TESTS_KEY, JSON.stringify(getOfflineTests().filter((t) => t.test.id !== id)));
  window.dispatchEvent(new CustomEvent(OFFLINE_TESTS_EVENT));
}

// ---------- Pending submissions ----------
const PENDING_KEY = 'isc-pending-results';
export interface PendingResult {
  test_id: string;
  test_title: string;
  answers: Record<number, number | null>;
  time_taken_seconds: number;
  queuedAt: number;
}

export function getPendingResults(): PendingResult[] {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

export function queueResult(r: PendingResult): void {
  localStorage.setItem(PENDING_KEY, JSON.stringify([...getPendingResults(), r]));
}

export function setPendingResults(list: PendingResult[]): void {
  localStorage.setItem(PENDING_KEY, JSON.stringify(list));
}
